import React, { useCallback, useRef, useMemo } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import { isEqual } from 'lodash';
// Import configs and util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';

// Import shared components
import { Button, Form } from '../../../../components';

// Import modules from this directory
import SignatureServices from './SignatureServices/SignatureServices';
import css from './EditListingMoreInfoForm.module.css';
import { withViewport } from '../../../../util/uiHelpers';

export const EditListingMoreInfoFormComponent = props => {
  // Track last submitted values to prevent redundant API calls
  const lastSubmittedValues = useRef({});
  // Flag to prevent concurrent update operations
  const isUpdating = useRef(false);
  // Reference to timeout ID for cleanup
  const saveTimeoutRef = useRef(null);

  // Create a stable handleLiveSubmit function that doesn't change on re-renders
  const handleLiveSubmit = useCallback(
    async (values, onLiveSubmit) => {
      // Clear any pending save operations
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Only proceed if we're not already updating and values have actually changed
      if (!isUpdating.current && !isEqual(values, lastSubmittedValues.current)) {
        saveTimeoutRef.current = setTimeout(async () => {
          try {
            isUpdating.current = true;
            await onLiveSubmit({ publicData: values });
            // Store the successfully submitted values
            lastSubmittedValues.current = JSON.parse(JSON.stringify(values));
          } catch (error) {
            console.error('Autosave failed:', error);
          } finally {
            isUpdating.current = false;
            saveTimeoutRef.current = null;
          }
        }, 1000); // Slightly longer debounce for fewer saves
      }
    },
    [] // Empty dependency array ensures this function never changes
  );

  // Memoize form render function to prevent unnecessary renders
  const renderForm = useMemo(
    () => formRenderProps => {
      const {
        formId,
        className,
        disabled,
        ready,
        handleSubmit,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        form,
        viewport,
        onLiveSubmit,
      } = formRenderProps;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;
      const { updateListingError, showListingsError } = fetchErrors || {};

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          {updateListingError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingMoreInfoForm.updateFailed" />
            </p>
          ) : null}
          {showListingsError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingMoreInfoForm.showListingFailed" />
            </p>
          ) : null}

          <SignatureServices viewport={viewport} values={values} form={form} />

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    },
    [] // This function should not change
  );

  // Clean up any pending timeouts when component unmounts
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={renderForm}
      // Initialize form with last submitted values if available
      initialValuesEqual={isEqual}
    />
  );
};

EditListingMoreInfoFormComponent.defaultProps = {
  fetchErrors: null,
  listingMinimumPriceSubUnits: 0,
  formId: 'EditListingMoreInfoForm',
};

EditListingMoreInfoFormComponent.propTypes = {
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onLiveSubmit: func.isRequired,
  marketplaceCurrency: string.isRequired,
  listingMinimumPriceSubUnits: number,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl, withViewport)(EditListingMoreInfoFormComponent);
